import React, { useCallback, useMemo, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSelector } from "react-redux";

import {
  HouseholdAccount,
  HouseholdAccountTypePagination,
} from "@fartherfinance/frontend/api/Households/types";
import { PaginationMetadata } from "@fartherfinance/frontend/api/Types";

import { getAccountProviderDisplayName } from "../../helpers";
import { unknownBalanceValue } from "@src/constants/unknownBalanceValue";
import {
  AccountMenuActions,
  householdAccountsColumns,
} from "@src/multiCustodian/components/Advisor/Households/constants";
import { useGetGenericPagination } from "@src/multiCustodian/hooks/useGetGenericPagination";
import copyToClipboard from "@src/multiCustodian/utils/copyToClipboard";
import Chip from "@src/sharedComponents/Chip/Chip";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import Text from "@src/sharedComponents/Typography/Text";
import { State } from "@src/store";
import toUSD from "@src/utils/toUSD";

import HouseholdAccountsModalManager from "./HouseholdAccountsModalManager";

import styles from "./HouseholdAccountsTable.module.css";

interface HouseholdAccountsTableProps {
  accounts: HouseholdAccount[] | undefined;
  pagination: PaginationMetadata | undefined;
  isLoading?: boolean;
  currentPage?: number;
  handleOnPageChange?: (
    page: number,
    accountType: HouseholdAccountTypePagination
  ) => void;
  accountType: HouseholdAccountTypePagination;
}

function EmptyCellWrapper({
  value,
  children,
}: {
  value: any;
  children: React.ReactNode;
}) {
  if (value) {
    return <>{children}</>;
  }
  return <span>{unknownBalanceValue}</span>;
}

const HouseholdAccountsTable: React.FC<HouseholdAccountsTableProps> = ({
  accounts,
  pagination,
  isLoading = false,
  currentPage = 1,
  handleOnPageChange,
  accountType,
}) => {
  const [_currentView, _setCurrentView] = useState<AccountMenuActions | null>(
    null
  );
  const smallXIcon = useSelector(
    (state: State) => state.main_Reducer.brand.current.images.small_x_icon
  );

  const [selectedAccount, setSelectedAccount] =
    useState<HouseholdAccount | null>(null);

  const _handleActionSelected = (
    action: AccountMenuActions,
    account: HouseholdAccount
  ) => {
    _setCurrentView(action);
    setSelectedAccount(account);
  };

  const closeModal = () => {
    setSelectedAccount(null);
  };

  const handleCopyToClipBoard = useCallback(
    (content: string) => () => {
      copyToClipboard(content, smallXIcon, "Custodian Account Number");
    },
    [smallXIcon]
  );

  const tableRowData = useMemo(() => {
    return (
      accounts?.map((account) => ({
        key: account.accountId,
        accountName: {
          value: (
            <EmptyCellWrapper value={account.name}>
              <Text variant="body" className={styles.accountValue}>
                {account.name}
              </Text>
            </EmptyCellWrapper>
          ),
        },
        number: {
          value: (
            <EmptyCellWrapper value={account.number}>
              <span
                onClick={handleCopyToClipBoard(account.number as string)}
                className={styles.copyCell}
              >
                <Text variant="body">{account.number}</Text>
                <ContentCopyIcon
                  classes={{
                    root: styles.copyIcon,
                  }}
                />
              </span>
            </EmptyCellWrapper>
          ),
        },
        owner: {
          value: (
            <EmptyCellWrapper value={account.member.firstName}>
              <Text variant="body">
                {account.member.firstName} {account.member.lastName}
              </Text>
            </EmptyCellWrapper>
          ),
        },
        type: {
          value: (
            <Chip
              label={account.type ?? unknownBalanceValue}
              variant="outlined"
              size="small"
            />
          ),
        },
        custodian: {
          value: (
            <EmptyCellWrapper value={account.custodian}>
              <Text variant="body">{account.custodian}</Text>
            </EmptyCellWrapper>
          ),
        },
        // modelPortfolio: {
        //   value: (
        //     <Text variant="body" className={styles.modelPortfolio}>
        //       {account.portfolio !== null
        //         ? account.portfolio.type === "Portfolio-Plan"
        //           ? account.portfolio.portfolioPlan.name
        //           : account.portfolio.portfolioFamily.name
        //         : "--"}
        //     </Text>
        //   ),
        // },
        amount: {
          value: (
            <Text variant="body" className={styles.amount}>
              {toUSD(account.amount.toNumber())}
            </Text>
          ),
        },
      })) || []
    );
  }, [accounts, handleCopyToClipBoard]);

  const columns = useMemo(() => {
    return householdAccountsColumns.map((column) => {
      if (
        column.accessor === "accountName" &&
        accounts &&
        accounts.length > 0
      ) {
        return {
          ...column,
          label: getAccountProviderDisplayName(accounts[0]),
        };
      }
      return column;
    });
  }, [accounts]);

  const tablePagination = useGetGenericPagination({
    data: { pagination },
    currentPage,
    pageSize: pagination?.pageSize || 10,
  });

  return (
    <>
      <PaginatedTable
        isLoading={isLoading}
        columns={columns}
        rows={tableRowData}
        rowClassName={styles.tableRow}
        pagination={tablePagination}
        showEmptyRows={false}
        onPageChange={(page: number) => handleOnPageChange?.(page, accountType)}
      />
      <HouseholdAccountsModalManager
        currentView={_currentView}
        account={selectedAccount}
        onClose={closeModal}
      />
    </>
  );
};

export default HouseholdAccountsTable;
